import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Boxes, AlertTriangle, Trash2, ArrowUpDown } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useConsommables, useCreateConsommable, useUpdateConsommable, useDeleteConsommable,
  useAjusterStock, useMachines,
} from '@/hooks/useTextprint';
import {
  TYPE_CONSOMMABLE_LABELS, UNITE_CONSOMMABLE_LABELS,
  type ConsommableImpression, type TypeConsommable, type UniteConsommable,
} from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

export const ConsommablesPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [typeFilter, setTypeFilter] = useState<TypeConsommable | ''>('');
  const [sousAlerte, setSousAlerte] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [editing, setEditing]       = useState<ConsommableImpression | undefined>();
  const [error, setError]           = useState<string | null>(null);

  const { data, isLoading, refetch } = useConsommables({ typeConsommable: typeFilter || undefined, sousAlerte: sousAlerte || undefined, limit: 200 });
  const consommables = data?.data ?? [];
  const remove = useDeleteConsommable();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (c: ConsommableImpression) => { setEditing(c); setDialogOpen(true); };
  const openAdjust = (c: ConsommableImpression) => { setEditing(c); setAdjustOpen(true); };

  const onRemove = async (id: string, code: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer le consommable "${code}" ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const sousAlerteCount = consommables.filter((c) => Number(c.stockActuel) < Number(c.stockMinimum)).length;
  const valeurStockCents = consommables.reduce((s, c) => s + Number(c.stockActuel) * Number(BigInt(c.prixUnitaireCents ?? '0')), 0);

  return (
    <>
      <Topbar
        title="Stock consommables"
        subtitle={filiale ? `${consommables.length} référence(s) · ${filiale.fullName}` : 'Stock'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}><Plus size={14} /> Nouvelle référence</button>
          </>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">📦</div><div className="ds-kr-val">{consommables.length}</div><div className="ds-kr-lbl">Références</div></div>
          <div className="ds-kr kr-c4"><div className="ds-kr-ico">🚨</div><div className="ds-kr-val">{sousAlerteCount}</div><div className="ds-kr-lbl">Sous seuil</div></div>
          <div className="ds-kr kr-c5"><div className="ds-kr-ico">💰</div><div className="ds-kr-val">{formatNumber(valeurStockCents / 100)}</div><div className="ds-kr-lbl">Valeur stock (FCFA)</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as TypeConsommable | '')} style={{ width: 'auto' }}>
            <option value="">Tous types</option>
            {(Object.keys(TYPE_CONSOMMABLE_LABELS) as TypeConsommable[]).map((t) => <option key={t} value={t}>{TYPE_CONSOMMABLE_LABELS[t]}</option>)}
          </select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--tx-2)' }}>
            <input type="checkbox" checked={sousAlerte} onChange={(e) => setSousAlerte(e.target.checked)} />
            Sous seuil uniquement
          </label>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Consommables</div><div className="ds-table-sub">{consommables.length} référence{consommables.length > 1 ? 's' : ''}</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 120 }}>Code</th>
              <th>Désignation</th>
              <th style={{ width: 110 }}>Type</th>
              <th>Machine assoc.</th>
              <th className="ds-am" style={{ width: 100 }}>Stock</th>
              <th className="ds-am" style={{ width: 80 }}>Seuil</th>
              <th className="ds-am" style={{ width: 100 }}>Prix U.</th>
              <th style={{ width: 110 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && consommables.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <Boxes size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun consommable</p>
                  <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}><Plus size={12} /> Créer la première référence</button>
                </td></tr>
              )}
              {!isLoading && consommables.map((c) => {
                const alerte = Number(c.stockActuel) < Number(c.stockMinimum);
                return (
                  <tr key={c.id}>
                    <td><span className="ds-ref-code">{c.code}</span></td>
                    <td><div className="ds-entry-label">{c.designation}</div>{c.fournisseur && <div className="ds-entry-sub">{c.fournisseur}</div>}</td>
                    <td style={{ fontSize: 11 }}>{TYPE_CONSOMMABLE_LABELS[c.typeConsommable]}</td>
                    <td style={{ fontSize: 12 }}>{c.machineRef ? `${c.machineRef.code}` : '—'}</td>
                    <td className="ds-am" style={{ fontWeight: 700, color: alerte ? 'var(--danger)' : 'var(--tx)' }}>
                      {Number(c.stockActuel).toFixed(1)} {UNITE_CONSOMMABLE_LABELS[c.unite]}
                    </td>
                    <td className="ds-am" style={{ fontSize: 11, color: 'var(--tx-3)' }}>{Number(c.stockMinimum).toFixed(1)}</td>
                    <td className="ds-am" style={{ fontSize: 12 }}>{c.prixUnitaireCents ? formatMontant(c.prixUnitaireCents) : '—'}</td>
                    <td><div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => openAdjust(c)} className="ds-row-btn" title="Ajuster stock"><ArrowUpDown size={13} /></button>
                      <button onClick={() => openEdit(c)} className="ds-row-btn" title="Modifier"><Edit size={13} /></button>
                      <button onClick={() => onRemove(c.id, c.code)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ConsommableFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} consommable={editing} />
      <AjusterStockDialog open={adjustOpen} onClose={() => setAdjustOpen(false)} consommable={editing} />
    </>
  );
};

const ConsommableFormDialog = ({ open, onClose, consommable }: { open: boolean; onClose: () => void; consommable?: ConsommableImpression }) => {
  const isEdit = !!consommable;
  const [code, setCode]           = useState('');
  const [designation, setDesignation] = useState('');
  const [typeConsommable, setTypeConsommable] = useState<TypeConsommable>('encre');
  const [unite, setUnite]         = useState<UniteConsommable>('litre');
  const [stockActuel, setStockActuel] = useState('');
  const [stockMinimum, setStockMinimum] = useState('');
  const [prixU, setPrixU]         = useState('');
  const [fournisseur, setFournisseur] = useState('');
  const [delaiReappro, setDelaiReappro] = useState('');
  const [machineRefId, setMachineRefId] = useState('');
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState<string | null>(null);

  const { data: machinesData } = useMachines({ limit: 200 });
  const machines = machinesData?.data ?? [];
  const create = useCreateConsommable();
  const update = useUpdateConsommable();

  useEffect(() => {
    if (!open) return;
    if (consommable) {
      setCode(consommable.code); setDesignation(consommable.designation);
      setTypeConsommable(consommable.typeConsommable); setUnite(consommable.unite);
      setStockActuel(consommable.stockActuel.toString());
      setStockMinimum(consommable.stockMinimum.toString());
      setPrixU(consommable.prixUnitaireCents ? String(Number(BigInt(consommable.prixUnitaireCents)) / 100) : '');
      setFournisseur(consommable.fournisseur ?? '');
      setDelaiReappro(consommable.delaiReappro?.toString() ?? '');
      setMachineRefId(consommable.machineRefId ?? '');
      setNotes(consommable.notes ?? '');
    } else {
      setCode(''); setDesignation(''); setTypeConsommable('encre'); setUnite('litre');
      setStockActuel('0'); setStockMinimum('0'); setPrixU(''); setFournisseur('');
      setDelaiReappro(''); setMachineRefId(''); setNotes('');
    }
    setError(null);
  }, [open, consommable]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        code: code.trim(), designation: designation.trim(),
        typeConsommable, unite,
        stockActuel: stockActuel ? parseFloat(stockActuel) : 0,
        stockMinimum: stockMinimum ? parseFloat(stockMinimum) : 0,
        prixUnitaireCents: prixU ? Math.round(parseFloat(prixU) * 100) : undefined,
        fournisseur: fournisseur.trim() || undefined,
        delaiReappro: delaiReappro ? parseInt(delaiReappro, 10) : undefined,
        machineRefId: machineRefId || undefined,
        notes: notes.trim() || undefined,
      };
      if (isEdit && consommable) await update.mutateAsync({ id: consommable.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = code.trim() && designation.trim();

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${consommable?.code}` : 'Nouveau consommable'} subtitle="Textprint — stock" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Code *"><input value={code} onChange={(e) => setCode(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="CONS-ENC-CYAN" /></Field>
          <Field label="Type *">
            <select value={typeConsommable} onChange={(e) => setTypeConsommable(e.target.value as TypeConsommable)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(TYPE_CONSOMMABLE_LABELS) as TypeConsommable[]).map((t) => <option key={t} value={t}>{TYPE_CONSOMMABLE_LABELS[t]}</option>)}
            </select>
          </Field>
          <Field label="Unité *">
            <select value={unite} onChange={(e) => setUnite(e.target.value as UniteConsommable)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(UNITE_CONSOMMABLE_LABELS) as UniteConsommable[]).map((u) => <option key={u} value={u}>{UNITE_CONSOMMABLE_LABELS[u]}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Désignation *"><input value={designation} onChange={(e) => setDesignation(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="Encre cyan Konica C4080" /></Field>

        <Field label="Machine associée">
          <select value={machineRefId} onChange={(e) => setMachineRefId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
            <option value="">— Aucune (consommable générique) —</option>
            {machines.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.nom}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-4 gap-3">
          <Field label="Stock actuel"><input type="number" min="0" step="0.01" value={stockActuel} onChange={(e) => setStockActuel(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Seuil min."><input type="number" min="0" step="0.01" value={stockMinimum} onChange={(e) => setStockMinimum(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Prix U. (FCFA)"><input type="number" min="0" step="1" value={prixU} onChange={(e) => setPrixU(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Délai réappro (j)"><input type="number" min="0" value={delaiReappro} onChange={(e) => setDelaiReappro(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <Field label="Fournisseur"><input value={fournisseur} onChange={(e) => setFournisseur(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} /></Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>{isEdit ? 'Enregistrer' : 'Créer'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const AjusterStockDialog = ({ open, onClose, consommable }: { open: boolean; onClose: () => void; consommable?: ConsommableImpression }) => {
  const [delta, setDelta]     = useState('');
  const [motif, setMotif]     = useState('');
  const [error, setError]     = useState<string | null>(null);
  const ajuster = useAjusterStock();

  useEffect(() => { if (open) { setDelta(''); setMotif(''); setError(null); } }, [open]);

  if (!consommable) return null;

  const stockActuel = Number(consommable.stockActuel);
  const newStock = stockActuel + (parseFloat(delta) || 0);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await ajuster.mutateAsync({ id: consommable.id, payload: { delta: parseFloat(delta), motif: motif.trim() || undefined } });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Ajuster stock — ${consommable.code}`} subtitle={consommable.designation} size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <div style={{ background: 'var(--p-soft)', padding: 12, borderRadius: 6, fontSize: 13 }}>
          Stock actuel : <strong>{stockActuel.toFixed(1)} {UNITE_CONSOMMABLE_LABELS[consommable.unite]}</strong>
          {delta && (
            <div style={{ marginTop: 4, fontSize: 12, color: newStock < 0 ? 'var(--danger)' : 'var(--tx-2)' }}>
              → Nouveau : <strong>{newStock.toFixed(1)} {UNITE_CONSOMMABLE_LABELS[consommable.unite]}</strong>
              {newStock < 0 && ' ⚠ Stock négatif refusé'}
            </div>
          )}
        </div>

        <Field label="Delta * (positif = entrée, négatif = sortie)">
          <input type="number" step="0.01" value={delta} onChange={(e) => setDelta(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="-5 ou +20" />
        </Field>

        <Field label="Motif">
          <input value={motif} onChange={(e) => setMotif(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="ex : consommation OF TX-OF-2026-0042" />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!delta || newStock < 0 || ajuster.isPending}>Valider</button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
